/**
 * Validation Engine Service Contract
 *
 * Defines the interface for the Validation Engine (12-compliance-service).
 * Must match: 12-compliance-service/proto/validation.proto
 *
 * BREAKING CHANGE LOG:
 * - 2026-01-10: Initial version (Issue #233)
 */

import { z } from 'zod';

// ============================================================================
// ENUMS
// ============================================================================

export enum ValidationRuleType {
  REQUIRED_FIELD = 'REQUIRED_FIELD',
  FORMAT = 'FORMAT',
  RANGE = 'RANGE',
  COMPARISON = 'COMPARISON',
  CROSS_SOURCE = 'CROSS_SOURCE',
  GEO_FENCE = 'GEO_FENCE',
  ADDRESS_VALIDATION = 'ADDRESS_VALIDATION',
  CUSTOM_FORMULA = 'CUSTOM_FORMULA',
  AI_CHECK = 'AI_CHECK',
}

export enum FailureAction {
  WARN = 'WARN',
  BLOCK = 'BLOCK',
  FLAG = 'FLAG',
  NOTIFY = 'NOTIFY',
}

export enum ValidationStatus {
  PASSED = 'passed',
  FAILED = 'failed',
  WARNING = 'warning',
  ERROR = 'error',
  SKIPPED = 'skipped',
}

export enum AddressDeliverability {
  DELIVERABLE = 'deliverable',
  UNDELIVERABLE = 'undeliverable',
  MISSING_UNIT = 'missing_unit',
  UNKNOWN = 'unknown',
}

export enum ComparisonOperator {
  EQ = 'eq',
  NE = 'ne',
  GT = 'gt',
  GTE = 'gte',
  LT = 'lt',
  LTE = 'lte',
}

export enum ComparisonType {
  EXACT = 'exact',
  FUZZY = 'fuzzy',
  NUMERIC_TOLERANCE = 'numeric_tolerance',
  PERCENTAGE = 'percentage',
}

export enum FormatType {
  EMAIL = 'email',
  PHONE = 'phone',
  SSN = 'ssn',
  EIN = 'ein',
  ZIP = 'zip',
  DATE = 'date',
  CURRENCY = 'currency',
  CUSTOM = 'custom',
}

export enum AiCheckType {
  FRAUD_DETECTION = 'fraud_detection',
  DOCUMENT_AUTHENTICITY = 'document_authenticity',
  SENTIMENT = 'sentiment',
  CUSTOM = 'custom',
}

export enum AddressProvider {
  GOOGLE_MAPS = 'google_maps',
  SMARTY_STREETS = 'smarty_streets',
  MELISSA = 'melissa',
}

// ============================================================================
// RULE CONFIGURATION SCHEMAS (type-specific)
// ============================================================================

export const RequiredFieldConfigSchema = z.object({
  fieldName: z.string().min(1, 'Field name is required'),
  allowEmpty: z.boolean().default(false),
  customMessage: z.string().optional(),
});

export const FormatConfigSchema = z.object({
  fieldName: z.string().min(1, 'Field name is required'),
  format: z.nativeEnum(FormatType),
  customPattern: z.string().optional(),
  customMessage: z.string().optional(),
});

export const RangeConfigSchema = z.object({
  fieldName: z.string().min(1, 'Field name is required'),
  min: z.number().optional(),
  max: z.number().optional(),
  inclusive: z.boolean().default(true),
  customMessage: z.string().optional(),
});

export const ComparisonConfigSchema = z.object({
  fieldName: z.string().min(1, 'Field name is required'),
  operator: z.nativeEnum(ComparisonOperator),
  compareToField: z.string().optional(),
  compareToValue: z.union([z.string(), z.number()]).optional(),
  multiplier: z.number().optional(), // For LTV-style checks: field1 <= field2 * multiplier
  customMessage: z.string().optional(),
});

export const CrossSourceConfigSchema = z.object({
  primaryFieldPath: z.string().min(1, 'Primary field path is required'),
  comparisonFieldPath: z.string().min(1, 'Comparison field path is required'),
  comparisonType: z.nativeEnum(ComparisonType),
  tolerance: z.number().optional(),
  tolerancePercent: z.number().optional(),
  customMessage: z.string().optional(),
});

export const GeoFenceConfigSchema = z.object({
  addressFieldPath: z.string().min(1, 'Address field path is required'),
  includedStates: z.array(z.string()).optional(),
  excludedStates: z.array(z.string()).optional(),
  includedZips: z.array(z.string()).optional(),
  excludedZips: z.array(z.string()).optional(),
  excludedFloodZones: z.array(z.string()).optional(),
  customMessage: z.string().optional(),
});

export const AddressValidationConfigSchema = z.object({
  addressFieldPath: z.string().min(1, 'Address field path is required'),
  provider: z.nativeEnum(AddressProvider).default(AddressProvider.SMARTY_STREETS),
  requireDeliverable: z.boolean().default(true),
  storeNormalized: z.boolean().default(true),
  checkFloodZone: z.boolean().default(false),
  customMessage: z.string().optional(),
});

export const CustomFormulaConfigSchema = z.object({
  formula: z.string().min(1, 'Formula is required'),
  fieldDependencies: z.array(z.string()),
  customMessage: z.string().optional(),
});

export const AiCheckConfigSchema = z.object({
  checkType: z.nativeEnum(AiCheckType),
  modelId: z.string().optional(),
  prompt: z.string().optional(),
  threshold: z.number().min(0).max(1).default(0.7),
  customMessage: z.string().optional(),
});

// ============================================================================
// TYPE EXPORTS FOR RULE CONFIGURATIONS
// ============================================================================

export type RequiredFieldConfig = z.infer<typeof RequiredFieldConfigSchema>;
export type FormatConfig = z.infer<typeof FormatConfigSchema>;
export type RangeConfig = z.infer<typeof RangeConfigSchema>;
export type ComparisonConfig = z.infer<typeof ComparisonConfigSchema>;
export type CrossSourceConfig = z.infer<typeof CrossSourceConfigSchema>;
export type GeoFenceConfig = z.infer<typeof GeoFenceConfigSchema>;
export type AddressValidationConfig = z.infer<typeof AddressValidationConfigSchema>;
export type CustomFormulaConfig = z.infer<typeof CustomFormulaConfigSchema>;
export type AiCheckConfig = z.infer<typeof AiCheckConfigSchema>;

// ============================================================================
// ADDRESS TYPES
// ============================================================================

export interface AddressInput {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
}

export interface NormalizedAddress extends AddressInput {
  normalizedStreet1?: string;
  normalizedStreet2?: string;
  normalizedCity?: string;
  normalizedState?: string;
  normalizedZip?: string;
  zipPlus4?: string;
  formattedAddress?: string;
  latitude?: number;
  longitude?: number;
  geoAccuracy?: string;
  deliverability?: AddressDeliverability;
  residential?: boolean;
  floodZone?: string;
  confidence?: number;
}

export const AddressInputSchema = z.object({
  street1: z.string().min(1, 'Street address is required'),
  street2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zip: z.string().min(1, 'ZIP code is required'),
  country: z.string().default('US'),
});

export const NormalizedAddressSchema = AddressInputSchema.extend({
  normalizedStreet1: z.string().optional(),
  normalizedStreet2: z.string().optional(),
  normalizedCity: z.string().optional(),
  normalizedState: z.string().optional(),
  normalizedZip: z.string().optional(),
  zipPlus4: z.string().optional(),
  formattedAddress: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  geoAccuracy: z.string().optional(),
  deliverability: z.nativeEnum(AddressDeliverability).optional(),
  residential: z.boolean().optional(),
  floodZone: z.string().optional(),
  confidence: z.number().min(0).max(1).optional(),
});

// ============================================================================
// VALIDATION RESULT TYPES
// ============================================================================

export interface ValidationResultItem {
  ruleId: string;
  ruleName: string;
  ruleType: ValidationRuleType;
  passed: boolean;
  result: ValidationStatus;
  message?: string | undefined;
  details?: Record<string, unknown> | undefined;
  suggestedAction?: string | undefined;
  confidence?: number | undefined;
  validatedAt: string;
}

export const ValidationResultItemSchema = z.object({
  ruleId: z.string(),
  ruleName: z.string(),
  ruleType: z.nativeEnum(ValidationRuleType),
  passed: z.boolean(),
  result: z.nativeEnum(ValidationStatus),
  message: z.string().optional(),
  details: z.record(z.string(), z.unknown()).optional(),
  suggestedAction: z.string().optional(),
  confidence: z.number().min(0).max(1).optional(),
  validatedAt: z.string(),
});

// ============================================================================
// CONFIDENCE SCORING TYPES
// ============================================================================

export interface ConfidenceFactor {
  name: string;
  weight: number;
  sourceRuleId?: string;
}

export interface ConfidenceTier {
  name: string;
  minScore: number;
  maxScore: number;
  color: string;
}

export interface ConfidenceScoreResult {
  score: number;
  tier: string;
  factors: Array<{
    name: string;
    score: number;
    weight: number;
    contribution: number;
  }>;
  calculatedAt: string;
}

export const ConfidenceFactorSchema = z.object({
  name: z.string().min(1, 'Factor name is required'),
  weight: z.number().min(0).max(1),
  sourceRuleId: z.string().optional(),
});

export const ConfidenceTierSchema = z.object({
  name: z.string().min(1, 'Tier name is required'),
  minScore: z.number().min(0).max(1),
  maxScore: z.number().min(0).max(1),
  color: z.string(),
});

export const ConfidenceScoreResultSchema = z.object({
  score: z.number().min(0).max(1),
  tier: z.string(),
  factors: z.array(
    z.object({
      name: z.string(),
      score: z.number(),
      weight: z.number(),
      contribution: z.number(),
    }),
  ),
  calculatedAt: z.string(),
});

// ============================================================================
// REQUEST MESSAGES
// ============================================================================

export interface CreateValidationRuleRequest {
  tenant_id?: string;
  business_id: string;
  name: string;
  description?: string;
  category?: string;
  rule_type: ValidationRuleType;
  configuration: Record<string, unknown>;
  target_entity_id?: string;
  target_field_name?: string;
  severity?: string;
  failure_action?: FailureAction;
  failure_message?: string;
  priority?: number;
  enabled?: boolean;
}

export interface UpdateValidationRuleRequest {
  tenant_id?: string;
  business_id: string;
  rule_id: string;
  name?: string;
  description?: string;
  category?: string;
  configuration?: Record<string, unknown>;
  target_entity_id?: string;
  target_field_name?: string;
  severity?: string;
  failure_action?: FailureAction;
  failure_message?: string;
  priority?: number;
  enabled?: boolean;
}

export interface GetValidationRuleRequest {
  tenant_id?: string;
  business_id: string;
  rule_id: string;
}

export interface DeleteValidationRuleRequest {
  tenant_id?: string;
  business_id: string;
  rule_id: string;
}

export interface ListValidationRulesRequest {
  tenant_id?: string;
  business_id: string;
  entity_id?: string;
  rule_type?: ValidationRuleType;
  enabled?: boolean;
}

export interface ExecuteValidationRequest {
  tenant_id?: string;
  business_id: string;
  entity_record_id?: string;
  document_id?: string;
  data: Record<string, unknown>;
  rule_ids?: string[];
  related_data?: Record<string, Record<string, unknown>>;
}

export interface ValidateAddressRequest {
  tenant_id?: string;
  business_id: string;
  address: AddressInput;
  provider?: AddressProvider;
  store_result?: boolean;
  entity_record_id?: string;
  field_name?: string;
}

export interface AutocompleteAddressRequest {
  tenant_id?: string;
  business_id: string;
  partial: string;
  session_token?: string;
}

export interface GetValidationResultsRequest {
  tenant_id?: string;
  business_id: string;
  entity_record_id?: string;
  rule_id?: string;
  passed?: boolean;
  limit?: number;
  offset?: number;
}

export interface CalculateConfidenceRequest {
  tenant_id?: string;
  business_id: string;
  entity_record_id: string;
  config_id: string;
}

export interface GetConfidenceScoreRequest {
  tenant_id?: string;
  business_id: string;
  entity_record_id: string;
}

export interface TestValidationRuleRequest {
  tenant_id?: string;
  business_id: string;
  rule_id: string;
  test_data: Record<string, unknown>;
  related_data?: Record<string, Record<string, unknown>>;
}

// ============================================================================
// RESPONSE MESSAGES
// ============================================================================

export interface ValidationRuleResponse {
  id: string;
  business_id: string;
  name: string;
  description?: string | undefined;
  category?: string | undefined;
  rule_type: ValidationRuleType;
  configuration: Record<string, unknown>;
  target_entity_id?: string | undefined;
  target_field_name?: string | undefined;
  severity: string;
  failure_action: FailureAction;
  failure_message?: string | undefined;
  priority: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string | undefined;
}

export interface CreateValidationRuleResponse {
  id: string;
  created_at: string;
}

export interface UpdateValidationRuleResponse {
  updated_at: string;
}

export interface ListValidationRulesResponse {
  rules: ValidationRuleResponse[];
  total: number;
}

export interface ExecuteValidationResponse {
  results: ValidationResultItem[];
  passed_count: number;
  failed_count: number;
  warning_count: number;
  overall_passed: boolean;
}

export interface ValidateAddressResponse {
  is_valid: boolean;
  normalized: NormalizedAddress;
  original: AddressInput;
  provider: AddressProvider;
  issues: string[];
}

export interface AutocompleteAddressResponse {
  suggestions: string[];
}

export interface GetValidationResultsResponse {
  results: ValidationResultItem[];
  total: number;
}

export interface CalculateConfidenceResponse extends ConfidenceScoreResult {}

export interface GetConfidenceScoreResponse {
  scores: ConfidenceScoreResult[];
}

export interface TestValidationRuleResponse {
  rule_id: string;
  rule_name: string;
  result: ValidationResultItem;
  execution_time_ms: number;
}

// ============================================================================
// ZOD SCHEMAS FOR REQUEST VALIDATION
// ============================================================================

export const createValidationRuleRequestSchema = z.object({
  tenant_id: z.string().optional(),
  business_id: z.string().min(1, 'Business ID is required'),
  name: z.string().min(1, 'Rule name is required'),
  description: z.string().optional(),
  category: z.string().optional(),
  rule_type: z.nativeEnum(ValidationRuleType),
  configuration: z.record(z.string(), z.unknown()),
  target_entity_id: z.string().optional(),
  target_field_name: z.string().optional(),
  severity: z.enum(['info', 'warning', 'critical']).optional().default('warning'),
  failure_action: z.nativeEnum(FailureAction).optional().default(FailureAction.FLAG),
  failure_message: z.string().optional(),
  priority: z.number().int().optional().default(0),
  enabled: z.boolean().optional().default(true),
});

export const updateValidationRuleRequestSchema = z.object({
  tenant_id: z.string().optional(),
  business_id: z.string().min(1, 'Business ID is required'),
  rule_id: z.string().min(1, 'Rule ID is required'),
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  category: z.string().optional(),
  configuration: z.record(z.string(), z.unknown()).optional(),
  target_entity_id: z.string().optional(),
  target_field_name: z.string().optional(),
  severity: z.enum(['info', 'warning', 'critical']).optional(),
  failure_action: z.nativeEnum(FailureAction).optional(),
  failure_message: z.string().optional(),
  priority: z.number().int().optional(),
  enabled: z.boolean().optional(),
});

export const getValidationRuleRequestSchema = z.object({
  tenant_id: z.string().optional(),
  business_id: z.string().min(1, 'Business ID is required'),
  rule_id: z.string().min(1, 'Rule ID is required'),
});

export const deleteValidationRuleRequestSchema = z.object({
  tenant_id: z.string().optional(),
  business_id: z.string().min(1, 'Business ID is required'),
  rule_id: z.string().min(1, 'Rule ID is required'),
});

export const listValidationRulesRequestSchema = z.object({
  tenant_id: z.string().optional(),
  business_id: z.string().min(1, 'Business ID is required'),
  entity_id: z.string().optional(),
  rule_type: z.nativeEnum(ValidationRuleType).optional(),
  enabled: z.boolean().optional(),
});

export const executeValidationRequestSchema = z.object({
  tenant_id: z.string().optional(),
  business_id: z.string().min(1, 'Business ID is required'),
  entity_record_id: z.string().optional(),
  document_id: z.string().optional(),
  data: z.record(z.string(), z.unknown()),
  rule_ids: z.array(z.string()).optional(),
  related_data: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
});

export const validateAddressRequestSchema = z.object({
  tenant_id: z.string().optional(),
  business_id: z.string().min(1, 'Business ID is required'),
  address: AddressInputSchema,
  provider: z.nativeEnum(AddressProvider).optional(),
  store_result: z.boolean().optional().default(true),
  entity_record_id: z.string().optional(),
  field_name: z.string().optional(),
});

export const autocompleteAddressRequestSchema = z.object({
  tenant_id: z.string().optional(),
  business_id: z.string().min(1, 'Business ID is required'),
  partial: z.string().min(1, 'Partial address is required'),
  session_token: z.string().optional(),
});

export const getValidationResultsRequestSchema = z.object({
  tenant_id: z.string().optional(),
  business_id: z.string().min(1, 'Business ID is required'),
  entity_record_id: z.string().optional(),
  rule_id: z.string().optional(),
  passed: z.boolean().optional(),
  limit: z.number().int().positive().optional().default(50),
  offset: z.number().int().nonnegative().optional().default(0),
});

export const calculateConfidenceRequestSchema = z.object({
  tenant_id: z.string().optional(),
  business_id: z.string().min(1, 'Business ID is required'),
  entity_record_id: z.string().min(1, 'Entity record ID is required'),
  config_id: z.string().min(1, 'Config ID is required'),
});

export const getConfidenceScoreRequestSchema = z.object({
  tenant_id: z.string().optional(),
  business_id: z.string().min(1, 'Business ID is required'),
  entity_record_id: z.string().min(1, 'Entity record ID is required'),
});

export const testValidationRuleRequestSchema = z.object({
  tenant_id: z.string().optional(),
  business_id: z.string().min(1, 'Business ID is required'),
  rule_id: z.string().min(1, 'Rule ID is required'),
  test_data: z.record(z.string(), z.unknown()),
  related_data: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
});

// ============================================================================
// CONTRACT METADATA
// ============================================================================

export const VALIDATION_CONTRACT_VERSION = '1.0.0';

export const ValidationContractMetadata = {
  service: '12-compliance-service',
  version: VALIDATION_CONTRACT_VERSION,
  description: 'Validation Engine - Geo-fence, cross-referencing, confidence scoring',
  issueReference: 'https://github.com/leanopx/ops-matrix/issues/233',
};
